import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import AreaFieldItemLoader from "./AreaFieldItemLoader.tsx";
import type { FieldItemProps } from "./areaFiledItem.types.ts";
import FieldItemActionButton from "./FieldItemActionButton.tsx";
import FieldItemCaptions from "./FieldItemCaptions.tsx";
import FieldItemOptionsButton from "./FieldItemOptionsButton.tsx";

const AreaFieldItem = <T,>({
  field,
  canBeRemoved,
  onRemoveItem,
  removeButtonLabel = "Remove field",
  hideOptions,
  isLoading,
  readonly,
  formatCaption,
  onShowOptions,
  optionRef,
  disableEqualitySelection,
  autoShowItemOptionsId,
  onSetAutoShowItemOptionsId,
  children,
}: FieldItemProps<T>) => {
  const [optionElementRef, setOptionElementRef] = useState<HTMLElement | null>(null);

  useEffect(() => {
    optionRef?.call(null, optionElementRef);
  }, [optionRef, optionElementRef]);

  const showOptions = useCallback(() => {
    if (!onShowOptions) return false;
    const ref = optionElementRef;
    if (!ref) return false;

    onShowOptions({ field: field, ref, showOnlySelected: false });
    return true;
  }, [optionElementRef, field, onShowOptions]);

  useEffect(() => {
    if (!autoShowItemOptionsId) return;
    const item = field as { config: { guid: string; filter: { guid: string } }; guid: string };
    if (
      autoShowItemOptionsId === item?.guid ||
      item.config?.guid === autoShowItemOptionsId ||
      item.config?.filter?.guid === autoShowItemOptionsId
    ) {
      if (showOptions()) {
        onSetAutoShowItemOptionsId?.();
      }
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [autoShowItemOptionsId, field, showOptions]);

  return (
    <>
      {isLoading === true && <AreaFieldItemLoader />}
      <Box
        className="field-item-row"
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          minHeight: 30,
          width: "100%",
          overflow: "hidden",
          bgcolor: readonly ? "action.disabledBackground" : undefined,
        }}
      >
        <FieldItemCaptions
          field={field}
          formatCaption={formatCaption}
          disableEqualitySelection={disableEqualitySelection}
        />
        {canBeRemoved && !readonly && onRemoveItem && (
          <FieldItemActionButton
            className="field-remove-button"
            aria-label={removeButtonLabel}
            color="error"
            onClick={() => onRemoveItem(field)}
          >
            <CloseRoundedIcon />
          </FieldItemActionButton>
        )}
        {children}
        <FieldItemOptionsButton hideOptions={hideOptions} showOptions={showOptions} ref={setOptionElementRef} />
      </Box>
    </>
  );
};
export default AreaFieldItem;
